import React, { useMemo, useEffect } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import EditableTokenName from './components/TokenEditableName';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import RowActions from './components/RowActions';
import TablePage from 'src/presentation/components/pages/shared/TablePage';
import TokenColumn from './components/TokenColumn';
import useApiTokens from './hooks/useApiTokens';
import useSaveTokenName from 'src/presentation/components/pages/integrations/apiTokens/hooks/useSaveTokenName';
import { EmptyStateWrapper } from 'src/presentation/components/pages/shared/TableList';
import { Row } from 'src/presentation/components/forms/Layout';
import { isLoadingStatus, STATUS_FAILED } from 'src/presentation/hooks/useResource';
import { toastError } from 'src/actions/toast';

const DevDocumentationUrl = 'https://developer.gladly.com/rest/#section/Getting-Started/Creating-API-Tokens';

const filterFunc = (items, searchString) => {
  if (!searchString) {
    return items;
  }

  const nameMatch = item => item.name.toLowerCase().indexOf(searchString.toLowerCase()) > -1;
  return filter(items, item => nameMatch(item));
};

const columnConfig = [
  { attr: 'createdAtString', label: 'Created On', customColumnStyle: () => ({ width: '210px' }) },
  { attr: 'lastUsedAtString', label: 'Last Used On', customColumnStyle: () => ({ width: '210px' }) },
];

const ApiTokens = ({ root }): JSX.Element => {
  const dispatch = useDispatch();
  const { addToken, apiTokens, apiTokensErrors, apiTokensLoadingState, organization } = useApiTokens();

  const { saveHandler: onNameSave } = useSaveTokenName();

  const editableColumn = useMemo(
    () => [
      {
        attr: 'id',
        label: 'Name',
        customColumnStyle: () => ({ width: '170px' }),
        customStyle: () => ({ position: 'relative' }),
        customRenderer: item => {
          const id = get(item, 'id', '');
          const name = get(item, 'name');

          return <EditableTokenName dataAid={id} onSave={newName => onNameSave(item, newName)} value={name} />;
        },
      },
      {
        attr: 'token',
        label: 'Token',
        customColumnStyle: () => ({ width: '300px' }),
        customRenderer: token => <TokenColumn apiToken={token} />,
      },
    ],
    [onNameSave]
  );

  const columns = [...editableColumn, ...columnConfig];

  useEffect(() => {
    if (apiTokensLoadingState === STATUS_FAILED) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
    }
  }, [apiTokensLoadingState, dispatch]);

  const actions = ({ 'data-aid': dataAid }) => (
    <RouterLinkButton data-aid={`${dataAid}-createToken`} onClick={addToken} to={'#'}>
      Create Token
    </RouterLinkButton>
  );

  const description = (
    <div>
      Create a token to integrate with our API. You will only see the token once. For help getting started with our APIs
      visit our <ExternalLink data-aid="developer-help" to={DevDocumentationUrl} label="Developer Documentation" />
      . <br />
      Example API request: GET{' '}
      <span data-aid="customer-api-url">{get(organization, 'gladlyUrl')}/api/v1/organization</span>.
    </div>
  );

  const EmptyState = (
    <StyledEmptyStateWrapper data-aid="api-tokens-emptyState">
      <Row>
        <EmptyStateText data-aid="emptyStateText">
          No API tokens have been created. Check out our{' '}
          <ExternalLink data-aid="emptyState-developer-help" to={DevDocumentationUrl} label="Developer Documentation" />
          .
        </EmptyStateText>
      </Row>
      <Row data-aid="emptyStateButton">{actions({ 'data-aid': 'api-tokens-emptyState' })}</Row>
    </StyledEmptyStateWrapper>
  );

  return (
    <TablePage
      actions={actions({ 'data-aid': 'api-tokens' })}
      columnConfig={columns}
      data-aid="admin-api-tokens-page"
      description={description}
      editor={null}
      emptyStateRender={EmptyState}
      errors={apiTokensErrors}
      filterFunc={filterFunc}
      isLoading={isLoadingStatus(apiTokensLoadingState)}
      items={apiTokens}
      name="API Tokens"
      resetErrors={null}
      root={root}
      renderRowActions={props => <RowActions {...props} />}
    />
  );
};

const StyledEmptyStateWrapper = styled(EmptyStateWrapper)`
  height: 50vh;
`;

const EmptyStateText = styled.span`
  text-align: center;
`;

export default ApiTokens;
